import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Save } from 'lucide-react';
import { Button, Select } from './ui.js';
import { Modal } from './Modal.js';
import { cn } from '../lib/cn.js';
import { currentLocale } from '../i18n.js';
import { formatDate, formatDateTime } from '../lib/format.js';

export interface DetailField {
  key: string;
  label: string;
  value: string;
  /**
   * `datetime-local` over a `date` beside a `time`: a moment is one decision,
   * and every mobile browser answers it with a single picker that sets the day
   * and the hour together. Two fields meant two pickers for one appointment,
   * and a day saved with the hour left blank.
   */
  type?: 'text' | 'select' | 'tel' | 'date' | 'time' | 'datetime-local' | 'number' | 'textarea';
  options?: Array<{ value: string; label: string }>;
  dir?: 'ltr' | 'rtl';
  /** Shown in the sheet but never editable — the API owns this value. */
  readOnly?: boolean;
  /**
   * Takes the whole row instead of half of one. Fields pair up two to a line
   * (see below); a free-text body has nothing to pair with and reads badly at
   * half width.
   */
  full?: boolean;
}

interface EditableDetailDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  fields?: DetailField[];
  onSave?: (updated: Record<string, string>) => void;
  children?: ReactNode;
  readOnly?: boolean;
  /** Open straight into the form, for callers whose command was "edit". */
  startInEdit?: boolean;
}

/**
 * What a field reads as when the sheet is being read rather than edited.
 *
 * A reader and an editor want different strings out of the same value: a select
 * posts an enum, and `<input type="date">` will only accept `YYYY-MM-DD`.
 * Neither is what someone opened the sheet to look at — the enum is untranslated
 * and the date is in an order no Israeli writes. Resolving it here rather than
 * at each call site keeps the posted value and its wording one declaration.
 */
function displayValue(field: DetailField): string {
  if (!field.value) return '—';
  if (field.type === 'select') {
    return field.options?.find((option) => option.value === field.value)?.label ?? field.value;
  }
  // A date field holds a wall-clock day with no zone. Pinning it to local
  // midnight keeps it on that day; `new Date('2026-08-20')` is read as UTC and
  // would slip a day back for anyone west of Greenwich.
  if (field.type === 'date') return formatDate(`${field.value}T00:00:00`, currentLocale());
  // Already a wall-clock local moment (`YYYY-MM-DDTHH:mm`), which `new Date`
  // reads as local — no zone to shift it, and none wanted: an appointment at
  // half past two is at half past two wherever it is read.
  if (field.type === 'datetime-local') return formatDateTime(field.value, currentLocale());
  return field.value;
}

/**
 * A detail sheet that flips to an inline edit form. Options carry a value and a
 * label separately, so a select can post an enum while showing its translation.
 *
 * Built on `Modal` rather than on its own overlay. The overlay it used to own
 * was a single scrolling box capped at `85vh`, and on a phone that is not 85%
 * of what the reader can see: mobile browsers resolve `vh` against the *large*
 * viewport, the one that assumes the URL bar has scrolled away, and this app
 * puts a 4rem navigation bar across the bottom on top of that. The last field
 * and the save button ended up under both. `Modal` already solves it — the
 * sheet rises from the bottom edge on a phone, the header and footer are fixed
 * while only the body scrolls, and the footer clears the home indicator with
 * `env(safe-area-inset-bottom)` — and it brings the focus trap and the Escape
 * handling this dialog never had.
 *
 * Fields are laid out two to a line. A label over a short value — a name, a
 * phone number, a date — leaves most of a dialog's width empty, and a column of
 * fifteen such rows is what pushed the button off the bottom in the first
 * place. Pairing them halves the height before any scrolling is needed.
 */
export function EditableDetailDialog({
  open,
  onClose,
  title,
  fields,
  onSave,
  children,
  readOnly,
  startInEdit,
}: EditableDetailDialogProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  const startEdit = () => {
    if (!fields) return;
    const next: Record<string, string> = {};
    for (const field of fields) {
      if (!field.readOnly) next[field.key] = field.value;
    }
    setValues(next);
    setEditing(true);
  };

  /*
   * Opened from an "edit" command, the dialog opens editable. It used to open
   * on the read-only view with its own edit button, so choosing Edit from a
   * row's menu put a second, identical press between the reader and the field
   * they had already said they wanted to change — and read as the menu item
   * doing nothing.
   */
  const armed = useRef(false);
  useEffect(() => {
    if (!open) {
      armed.current = false;
      return;
    }
    if (!startInEdit || armed.current || !fields?.length) return;
    armed.current = true;
    startEdit();
  }, [open, startInEdit, fields]);

  const save = () => {
    onSave?.(values);
    setEditing(false);
  };

  const handleClose = () => {
    setEditing(false);
    onClose();
  };

  const editable = Boolean(fields) && !readOnly;

  return (
    <Modal
      open={open}
      title={title}
      onClose={handleClose}
      footer={
        editable && (
          <Button
            variant={editing ? 'primary' : 'secondary'}
            onClick={editing ? save : startEdit}
            icon={editing ? <Save className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
          >
            {editing ? t('common.save') : t('common.edit')}
          </Button>
        )
      }
    >
      {children}
      {fields && !children && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
          {fields.map((field) => (
            <div key={field.key} className={cn('min-w-0', field.full && 'col-span-2')}>
              <span className="text-xs text-muted-foreground">{field.label}</span>
              {editing && !field.readOnly ? (
                field.type === 'select' && field.options ? (
                  <Select
                    value={values[field.key] ?? ''}
                    aria-label={field.label}
                    options={field.options}
                    onChange={(value) => setValues((prev) => ({ ...prev, [field.key]: value }))}
                    className="h-9 mt-1 bg-muted"
                  />
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={values[field.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full min-h-20 p-3 mt-1 rounded-lg bg-muted text-sm outline-none resize-y"
                    dir={field.dir}
                  />
                ) : (
                  <input
                    type={field.type ?? 'text'}
                    value={values[field.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full h-9 px-3 mt-1 rounded-lg bg-muted text-sm outline-none"
                    dir={field.dir}
                  />
                )
              ) : (
                /* `break-words` rather than `truncate`: half a dialog is narrow
                   enough that an address or a message would lose its tail, and
                   this is the screen someone opened to read it. */
                <p className="text-sm font-medium break-words" dir={field.dir}>
                  {displayValue(field)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
